import React, { useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, Menu, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { MoreHoriz, Send } from "@mui/icons-material";
import { TaskComment, TaskEvent, TaskPerson, taskService } from "../../../services/taskService";
import { isSubmitShortcut, SUBMIT_SHORTCUT_LABEL } from "../../../utils/keyboard";
import { useTaskStore } from "../TaskContext";
import { canDeleteComment, canEditComment } from "../taskPermissions";
import { apiErrorMessage, describeEvent, formatDateTime, personName, timeAgo } from "../taskUtils";
import { StaffAvatar } from "./TaskBits";

const QUIET_EVENT_LIMIT = 6;

interface TaskActivityProps {
    taskId: string;
    comments: TaskComment[];
    events: TaskEvent[];
    onCommentsChange: (comments: TaskComment[]) => void;
}

type Entry = { kind: "comment"; at: string; comment: TaskComment } | { kind: "event"; at: string; event: TaskEvent };

/** Comments and the task's history in one timeline, oldest first. */
export const TaskActivity: React.FC<TaskActivityProps> = ({ taskId, comments, events, onCommentsChange }) => {
    const { peopleById, today, viewer, toast } = useTaskStore();
    const [showAll, setShowAll] = useState(false);

    const entries = useMemo<Entry[]>(() => {
        const list: Entry[] = [
            ...comments.map((c) => ({ kind: "comment" as const, at: c.create_time, comment: c })),
            ...events.filter((e) => e.kind !== "comment").map((e) => ({ kind: "event" as const, at: e.create_time, event: e })),
        ];
        return list.sort((a, b) => a.at.localeCompare(b.at));
    }, [comments, events]);

    // Long histories: keep comments, fold older updates away.
    const quietCount = entries.filter((e) => e.kind === "event").length;
    const visible = useMemo(() => {
        if (showAll || quietCount <= QUIET_EVENT_LIMIT) return entries;
        let keepEvents = QUIET_EVENT_LIMIT;
        const kept: Entry[] = [];
        for (let i = entries.length - 1; i >= 0; i--) {
            const e = entries[i];
            if (e.kind === "comment" || i === 0) kept.unshift(e);
            else if (keepEvents > 0) {
                kept.unshift(e);
                keepEvents--;
            }
        }
        return kept;
    }, [entries, showAll, quietCount]);
    const hidden = entries.length - visible.length;

    const editComment = async (comment: TaskComment, body: string) => {
        try {
            const saved = await taskService.updateComment(comment.comment_id, body);
            onCommentsChange(comments.map((c) => (c.comment_id === saved.comment_id ? saved : c)));
        } catch (err) {
            toast({ message: apiErrorMessage(err, "Couldn't edit that comment."), severity: "error" });
        }
    };
    const deleteComment = async (comment: TaskComment) => {
        onCommentsChange(comments.filter((c) => c.comment_id !== comment.comment_id));
        try {
            await taskService.deleteComment(comment.comment_id);
        } catch (err) {
            onCommentsChange(comments);
            toast({ message: apiErrorMessage(err, "Couldn't delete that comment."), severity: "error" });
        }
    };

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Activity
            </Typography>
            <Stack spacing={1}>
                {visible.map((entry, i) => (
                    <React.Fragment key={entry.kind === "comment" ? entry.comment.comment_id : entry.event.event_id}>
                        {hidden > 0 && i === 1 && (
                            <Button size="small" onClick={() => setShowAll(true)} sx={{ alignSelf: "flex-start", textTransform: "none", ml: 3 }}>
                                Show {hidden} earlier update{hidden === 1 ? "" : "s"}
                            </Button>
                        )}
                        {entry.kind === "event" ? (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 0.25 }}>
                                <StaffAvatar person={entry.event.actor_user_id ? peopleById.get(entry.event.actor_user_id) : null} userId={entry.event.actor_user_id} size={20} />
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, minWidth: 0 }}>
                                    <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                                        {entry.event.actor_user_id ? personName(peopleById.get(entry.event.actor_user_id)) : "Someone"}
                                    </Box>{" "}
                                    {describeEvent(entry.event, peopleById, today)}
                                    <Tooltip title={formatDateTime(entry.event.create_time)}>
                                        <Box component="span" sx={{ color: "text.disabled", ml: 0.75 }}>
                                            {timeAgo(entry.event.create_time)}
                                        </Box>
                                    </Tooltip>
                                </Typography>
                            </Stack>
                        ) : (
                            <CommentBubble
                                comment={entry.comment}
                                author={entry.comment.author_user_id ? peopleById.get(entry.comment.author_user_id) : undefined}
                                people={peopleById}
                                canEdit={canEditComment(entry.comment, viewer)}
                                canDelete={canDeleteComment(entry.comment, viewer)}
                                onEdit={(body) => editComment(entry.comment, body)}
                                onDelete={() => deleteComment(entry.comment)}
                            />
                        )}
                    </React.Fragment>
                ))}
            </Stack>
        </Box>
    );
};

/** Highlight "@Name" for people on the team. */
function renderBody(body: string, people: Map<string, TaskPerson>): React.ReactNode {
    const names = Array.from(people.values())
        .map((p) => personName(p))
        .sort((a, b) => b.length - a.length)
        .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    if (!names.length) return body;
    const re = new RegExp(`@(${names.join("|")})`, "g");
    const parts: React.ReactNode[] = [];
    let last = 0;
    body.replace(re, (match, _name, offset: number) => {
        parts.push(body.slice(last, offset));
        parts.push(
            <Box key={offset} component="span" sx={{ color: "#18383E", fontWeight: 600, bgcolor: "#e6efed", borderRadius: 0.5, px: 0.25 }}>
                {match}
            </Box>,
        );
        last = offset + match.length;
        return match;
    });
    parts.push(body.slice(last));
    return parts;
}

interface CommentBubbleProps {
    comment: TaskComment;
    author?: TaskPerson;
    people: Map<string, TaskPerson>;
    canEdit: boolean;
    canDelete: boolean;
    onEdit: (body: string) => void;
    onDelete: () => void;
}

const CommentBubble: React.FC<CommentBubbleProps> = ({ comment, author, people, canEdit, canDelete, onEdit, onDelete }) => {
    const [menu, setMenu] = useState<HTMLElement | null>(null);
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(comment.body);

    return (
        <Stack direction="row" spacing={1} alignItems="flex-start">
            <StaffAvatar person={author} userId={comment.author_user_id} size={26} />
            <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, px: 1.5, py: 1, borderRadius: 2, bgcolor: "#fbfcfc" }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {author ? personName(author) : "Former staff member"}
                    </Typography>
                    <Tooltip title={formatDateTime(comment.create_time)}>
                        <Typography variant="caption" color="text.disabled">
                            {timeAgo(comment.create_time)}
                            {comment.edited_at ? " · edited" : ""}
                        </Typography>
                    </Tooltip>
                    <Box sx={{ flex: 1 }} />
                    {(canEdit || canDelete) && (
                        <IconButton size="small" onClick={(e) => setMenu(e.currentTarget)} aria-label="Comment options" sx={{ mr: -0.75 }}>
                            <MoreHoriz fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
                {editing ? (
                    <Box sx={{ mt: 0.75 }}>
                        <TextField
                            fullWidth
                            multiline
                            autoFocus
                            size="small"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => {
                                if (isSubmitShortcut(e.nativeEvent) && text.trim()) {
                                    e.preventDefault();
                                    onEdit(text.trim());
                                    setEditing(false);
                                } else if (e.key === "Escape") {
                                    e.stopPropagation();
                                    setEditing(false);
                                    setText(comment.body);
                                }
                            }}
                        />
                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 0.75 }}>
                            <Button size="small" color="inherit" onClick={() => { setEditing(false); setText(comment.body); }}>
                                Cancel
                            </Button>
                            <Button size="small" variant="contained" disabled={!text.trim()} onClick={() => { onEdit(text.trim()); setEditing(false); }}>
                                Save
                            </Button>
                        </Stack>
                    </Box>
                ) : (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", mt: 0.25 }}>
                        {renderBody(comment.body, people)}
                    </Typography>
                )}
            </Paper>
            <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
                {canEdit && (
                    <MenuItem onClick={() => { setMenu(null); setEditing(true); }}>Edit</MenuItem>
                )}
                {canDelete && (
                    <MenuItem sx={{ color: "error.main" }} onClick={() => { setMenu(null); onDelete(); }}>
                        Delete
                    </MenuItem>
                )}
            </Menu>
        </Stack>
    );
};

// ── Composer with @mentions ─────────────────────────────────────────────────

interface ComposerProps {
    taskId: string;
    onPosted: (comment: TaskComment) => void;
}

const MENTION_BEFORE_CARET = /(^|\s)@([^\s@]{0,30})$/;

export const CommentComposer: React.FC<ComposerProps> = ({ taskId, onPosted }) => {
    const { people, meId, toast } = useTaskStore();
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [mention, setMention] = useState<{ query: string; start: number } | null>(null);
    const [active, setActive] = useState(0);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    // Who was picked from the list — kept only if "@Name" is still in the text.
    const picked = useRef(new Map<string, string>());

    // Only people who can still be given work — not Investors or deactivated staff.
    const team = useMemo(() => people.filter((p) => p.assignable && p.user_id !== meId), [people, meId]);
    const matches = useMemo(() => {
        if (!mention) return [];
        const q = mention.query.toLowerCase();
        return team.filter((p) => personName(p).toLowerCase().split(" ").some((w) => w.startsWith(q)) || p.email.toLowerCase().startsWith(q)).slice(0, 6);
    }, [mention, team]);

    const detect = (value: string, caret: number) => {
        const m = MENTION_BEFORE_CARET.exec(value.slice(0, caret));
        if (m) {
            setMention({ query: m[2], start: caret - m[2].length - 1 });
            setActive(0);
        } else setMention(null);
    };

    const insert = (person: TaskPerson) => {
        if (!mention) return;
        const name = personName(person);
        const caret = mention.start + 1 + mention.query.length;
        const next = `${text.slice(0, mention.start)}@${name} ${text.slice(caret)}`;
        picked.current.set(person.user_id, name);
        setText(next);
        setMention(null);
        requestAnimationFrame(() => {
            const pos = mention.start + name.length + 2;
            inputRef.current?.focus();
            inputRef.current?.setSelectionRange(pos, pos);
        });
    };

    const send = async () => {
        const body = text.trim();
        if (!body || sending) return;
        const mentionIds = Array.from(picked.current.entries())
            .filter(([, name]) => body.includes(`@${name}`))
            .map(([id]) => id);
        setSending(true);
        try {
            const comment = await taskService.addComment(taskId, body, mentionIds);
            onPosted(comment);
            setText("");
            picked.current.clear();
        } catch (err) {
            toast({ message: apiErrorMessage(err, "Couldn't post the comment."), severity: "error" });
        } finally {
            setSending(false);
        }
    };

    return (
        <Box sx={{ position: "relative" }}>
            {mention && matches.length > 0 && (
                <Paper elevation={6} sx={{ position: "absolute", bottom: "100%", left: 0, mb: 0.75, width: 260, py: 0.5, zIndex: 2 }}>
                    {matches.map((p, i) => (
                        <MenuItem
                            key={p.user_id}
                            selected={i === active}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                insert(p);
                            }}
                            sx={{ gap: 1 }}
                        >
                            <StaffAvatar person={p} size={22} tooltip={false} />
                            <Typography variant="body2">{personName(p)}</Typography>
                        </MenuItem>
                    ))}
                </Paper>
            )}
            <Stack direction="row" spacing={1} alignItems="flex-end">
                <TextField
                    inputRef={inputRef}
                    fullWidth
                    multiline
                    maxRows={6}
                    size="small"
                    placeholder="Write a comment — type @ to mention someone"
                    value={text}
                    onChange={(e) => {
                        setText(e.target.value);
                        detect(e.target.value, e.target.selectionStart ?? e.target.value.length);
                    }}
                    onKeyDown={(e) => {
                        if (mention && matches.length) {
                            if (e.key === "ArrowDown") {
                                e.preventDefault();
                                setActive((i) => (i + 1) % matches.length);
                                return;
                            }
                            if (e.key === "ArrowUp") {
                                e.preventDefault();
                                setActive((i) => (i - 1 + matches.length) % matches.length);
                                return;
                            }
                            if (e.key === "Enter" || e.key === "Tab") {
                                e.preventDefault();
                                insert(matches[active]);
                                return;
                            }
                            if (e.key === "Escape") {
                                e.stopPropagation();
                                setMention(null);
                                return;
                            }
                        }
                        if (isSubmitShortcut(e.nativeEvent)) {
                            e.preventDefault();
                            send();
                        }
                    }}
                    onBlur={() => setMention(null)}
                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "background.paper", borderRadius: 2 } }}
                />
                <Tooltip title={`Send (${SUBMIT_SHORTCUT_LABEL})`}>
                    <span>
                        <IconButton color="primary" onClick={send} disabled={!text.trim() || sending} aria-label="Send comment">
                            <Send />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
        </Box>
    );
};
